import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Discreet, permanent warning shown to Premium/Enterprise users that
 * have no company_website set. It stays visible on every dashboard page
 * until the user adds a website in Settings → Profile.
 *
 * Intentionally subtle (not a blocking modal):
 * - Thin amber bar
 * - Single line of text
 * - Inline link to Settings
 */
export default function MissingWebsiteWarning() {
  const { user } = useAuth();
  const { language, isRTL } = useLanguage();

  if (!user) return null;
  // WO-21 — a MID2BID Admin is not a customer being vetted, so it is exempt
  // from the company-website requirement (see SettingsPage for the reasoning).
  if (user.role === 'admin') return null;
  const plan = (user.plan_id || 'free').toLowerCase();
  const requiresWebsite = plan === 'premium' || plan === 'enterprise';
  if (!requiresWebsite) return null;

  const hasWebsite =
    !!user.company_website || user.has_website === true;
  if (hasWebsite) return null;

  return (
    <div
      className="border-b border-amber-300/60 dark:border-amber-700/50 bg-amber-50/70 dark:bg-amber-950/30"
      role="status"
      data-testid="missing-website-warning"
    >
      <div className={`max-w-7xl mx-auto px-4 lg:px-8 py-2 flex items-center gap-2 text-xs text-amber-900 dark:text-amber-200 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1 truncate">
          {language === 'ar'
            ? 'خطتك الحالية تتطلب موقع إلكتروني للشركة. يرجى إضافته من'
            : 'Your current plan requires a company website. Please add it in'}{' '}
          <Link
            to="/dashboard/settings?tab=profile"
            className="font-semibold underline hover:no-underline"
            data-testid="missing-website-warning-link"
          >
            {language === 'ar' ? 'الإعدادات → الملف الشخصي' : 'Settings → Profile'}
          </Link>
          .
        </span>
      </div>
    </div>
  );
}
